var __jsx = React.createElement;
import React, { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import PromoPopup from './PromoPopup';
import PromoButton from './PromoButton';
import PromoDebugInfo from './PromoDebugInfo';
import { useEnhancedPromoSystem } from '../hooks/useEnhancedPromoSystem';
import { usePromoDisplayDelay } from '../hooks/usePromoDisplayDelay';
import { checkTestFlag } from "@/common/config/websiteConfig";

/**
 * Global PromoManager Component
 * 
 * This component serves as the centralized promo management system for the entire application.
 * It integrates drop-off tracking, promo eligibility determination, and banner display functionality
 * into a single global component that should be mounted at the application root level.
 * 
 * Key Features:
 * - Automatic drop-off detection across all pages
 * - Intelligent promo eligibility determination
 * - Cached promo code management
 * - Global banner display with customizable appearance
 * - Authentication-aware API requests
 * - Comprehensive analytics tracking
 * - Separated debug component for development
 */
var GlobalPromoManager = function GlobalPromoManager() {
  var location = useLocation();
  var _useEnhancedPromoSyst = useEnhancedPromoSystem(),
    promoCode = _useEnhancedPromoSyst.promoCode,
    error = _useEnhancedPromoSyst.error,
    isExpired = _useEnhancedPromoSyst.isExpired,
    eligibilityState = _useEnhancedPromoSyst.eligibilityState,
    dropOffState = _useEnhancedPromoSyst.dropOffState,
    isPopupOpen = _useEnhancedPromoSyst.isPopupOpen,
    setIsPopupOpen = _useEnhancedPromoSyst.setIsPopupOpen,
    shouldShowBanner = _useEnhancedPromoSyst.shouldShowBanner,
    handlePromoShown = _useEnhancedPromoSyst.handlePromoShown,
    handlePromoClosed = _useEnhancedPromoSyst.handlePromoClosed,
    handleBookNow = _useEnhancedPromoSyst.handleBookNow,
    handleButtonClick = _useEnhancedPromoSyst.handleButtonClick,
    resetPromoVisibility = _useEnhancedPromoSyst.resetPromoVisibility,
    markFirstDisplayComplete = _useEnhancedPromoSyst.markFirstDisplayComplete,
    trackNavigation = _useEnhancedPromoSyst.trackNavigation,
    displayConfig = _useEnhancedPromoSyst.displayConfig;

  // Track navigation changes for drop-off detection
  useEffect(function () {
    trackNavigation(location.pathname);
  }, [location.pathname]);

  // Apply delay logic for first-time display (popup or button)
  var _usePromoDisplayDelay = usePromoDisplayDelay({
      isEligible: shouldShowBanner && (displayConfig.showPopup || displayConfig.showButton),
      isFirstTime: dropOffState.first_time_visible,
      onFirstDisplayComplete: markFirstDisplayComplete
    }),
    shouldShowWithDelay = _usePromoDisplayDelay.shouldShowWithDelay,
    isDelayActive = _usePromoDisplayDelay.isDelayActive,
    remainingDelay = _usePromoDisplayDelay.remainingDelay;

  // Handle popup close with proper state management
  var handlePopupClose = useCallback(function () {
    setIsPopupOpen(false);
    handlePromoClosed();
  }, [setIsPopupOpen, handlePromoClosed]);

  // Handle button click - direct redirect to shop-apply
  var handlePromoButtonClick = useCallback(function () {
    if (!promoCode) return;

    // Direct call to the hook's handleButtonClick which will redirect
    handleButtonClick();
  }, [promoCode, handleButtonClick]);

  // Determine what should be shown based on delay logic
  var shouldShowPopup = displayConfig.showPopup && shouldShowWithDelay && isPopupOpen;
  var shouldShowButton = displayConfig.showButton && shouldShowWithDelay && !isPopupOpen;
  return /*#__PURE__*/React.createElement(React.Fragment, null, checkTestFlag("TEST_CUTOMIZED_PROMO_DEBUG") && /*#__PURE__*/React.createElement(PromoDebugInfo, {
    eligibilityState: eligibilityState,
    dropOffState: dropOffState,
    promoCode: promoCode,
    shouldShowBanner: shouldShowBanner,
    displayConfig: displayConfig,
    isPopupOpen: isPopupOpen,
    isExpired: isExpired,
    shouldShowWithDelay: shouldShowWithDelay,
    isDelayActive: isDelayActive,
    remainingDelay: remainingDelay,
    resetPromoVisibility: resetPromoVisibility,
    setIsPopupOpen: setIsPopupOpen,
    error: error
  }), shouldShowPopup && promoCode && shouldShowBanner && !isExpired && /*#__PURE__*/React.createElement(PromoPopup, {
    onClose: handlePopupClose,
    promoCode: promoCode,
    position: displayConfig.position,
    onBookNow: handleBookNow,
    onShow: handlePromoShown
  }), shouldShowButton && promoCode && shouldShowBanner && !isExpired && /*#__PURE__*/React.createElement(PromoButton, {
    promoCode: promoCode,
    onClick: handlePromoButtonClick
  }));
};
export default GlobalPromoManager;